import { useState } from "react";
import type { Lang } from "@calendar/shared";
import { seededApiKey } from "../api";

interface Props {
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  onLink: (apiKey: string, code: string) => Promise<void>;
  toast: (text: string, kind?: "ok" | "error") => void;
}

/**
 * The web client is not the device that owns the account, so it starts out
 * with nothing. It is paired with the Telegram bot by exchanging a short code
 * for a user id using the deployment's shared API key.
 *
 * The desktop shell ships that key in its config file, so the field is prefilled
 * there and the user only has to type the code. In a browser tab nothing is
 * seeded and the field is empty.
 */
export function LinkScreen(props: Props) {
  const { t, onLink, toast } = props;
  const [apiKey, setApiKey] = useState(() => seededApiKey());
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (apiKey.trim().length < 8 || code.trim().length < 4) {
      toast(t("error.generic"), "error");
      return;
    }
    setBusy(true);
    try {
      await onLink(apiKey.trim(), code.trim());
    } catch {
      toast(t("link.failed"), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <form className="dialog" onSubmit={submit}>
        <h1 className="dialog__title">🗓 Smart Calendar</h1>
        <p className="muted">{t("link.needCode")}</p>

        <div className="field">
          <label htmlFor="l-key">{t("settings.apiKey")}</label>
          <input
            id="l-key"
            className="input"
            type="password"
            value={apiKey}
            autoComplete="off"
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="l-code">{t("settings.code")}</label>
          <input
            id="l-code"
            className="input"
            value={code}
            maxLength={12}
            placeholder="ABCD1234"
            style={{ letterSpacing: "0.2em", textAlign: "center" }}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </div>

        <div className="dialog__actions">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {t("link.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
